import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiEdit, FiTrash2, FiDollarSign, FiSearch, FiFilter, FiDownload, FiCalendar } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import expenseService from '../services/expenseService';
import Button from '../components/Button';
import './Technicians.css';
import '../assets/styles/ListTable.css';

const LIMIT_OPTIONS = [10, 20, 50, 100];

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [limitOption, setLimitOption] = useState('10');
  const [customLimit, setCustomLimit] = useState(25);
  const searchDebounce = useRef(null);

  const categories = [
    'fuel',
    'repairs',
    'salaries',
    'rent',
    'utilities',
    'tools',
    'inventory',
    'marketing',
    'insurance',
    'other',
  ];

  const effectiveLimit = useMemo(() => {
    if (limitOption === 'custom') {
      const n = parseInt(customLimit, 10);
      return isNaN(n) ? 10 : Math.min(100, Math.max(1, n));
    }
    const n = parseInt(limitOption, 10);
    return isNaN(n) ? 10 : n;
  }, [limitOption, customLimit]);

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setAppliedSearch(searchTerm), 400);
    return () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); };
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearch, filterCategory, startDate, endDate, minAmount, maxAmount, effectiveLimit]);

  useEffect(() => {
    fetchExpenses();
  }, [currentPage, appliedSearch, filterCategory, startDate, endDate, minAmount, maxAmount, effectiveLimit]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: effectiveLimit };
      if (appliedSearch) params.search = appliedSearch;
      if (filterCategory !== 'all') params.category = filterCategory;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (minAmount) params.minAmount = minAmount;
      if (maxAmount) params.maxAmount = maxAmount;
      const response = await expenseService.getAll(params);
      setExpenses(response.data || []);
      setTotal(response.total ?? 0);
      setPages(response.pages ?? 1);
    } catch (error) {
      toast.error('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await expenseService.delete(id);
        toast.success('Expense deleted successfully');
        fetchExpenses();
      } catch (error) {
        toast.error('Failed to delete expense');
      }
    }
  };

  const getCategoryBadge = (category) => {
    const categoryColors = {
      fuel: 'badge-primary',
      repairs: 'badge-warning',
      salaries: 'badge-success',
      rent: 'badge-error',
      utilities: 'badge-info',
      tools: 'badge-gray',
      inventory: 'badge-primary',
      marketing: 'badge-warning',
      insurance: 'badge-success',
      other: 'badge-gray',
    };
    return `badge ${categoryColors[category] || 'badge-gray'}`;
  };

  const getPaymentMethodBadge = (method) => {
    const methodColors = {
      cash: 'badge-success',
      upi: 'badge-primary',
      card: 'badge-info',
      bank_transfer: 'badge-warning',
      cheque: 'badge-gray',
    };
    return `badge ${methodColors[method] || 'badge-gray'}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const pageTotalAmount = expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">Expenses</h2>
          <p className="page-subtitle">Track and manage your shop expenses</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <Link to="/expenses/reports">
            <Button variant="secondary" icon={<FiDownload />}>
              Reports
            </Button>
          </Link>
          <Link to="/expenses/new">
            <Button variant="primary" icon={<FiPlus />}>
              Add Expense
            </Button>
          </Link>
        </div>
      </div>

      <div className="page-filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by description, vendor, or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </option>
          ))}
        </select>

        <Button
          variant="secondary"
          icon={<FiFilter />}
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? 'Hide Filters' : 'More Filters'}
        </Button>
      </div>

      {showFilters && (
        <div className="page-filters" style={{ marginTop: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FiCalendar />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="filter-select"
                placeholder="Start Date"
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FiCalendar />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="filter-select"
                placeholder="End Date"
              />
            </div>
            <input
              type="number"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              className="filter-select"
              placeholder="Min Amount"
              style={{ width: '150px' }}
            />
            <input
              type="number"
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
              className="filter-select"
              placeholder="Max Amount"
              style={{ width: '150px' }}
            />
            <Button
              variant="secondary"
              onClick={() => {
                setStartDate('');
                setEndDate('');
                setMinAmount('');
                setMaxAmount('');
              }}
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {expenses.length === 0 ? (
        <div className="empty-state">
          <FiDollarSign className="empty-icon" />
          <h3>No Expenses Found</h3>
          <p>Get started by adding your first expense</p>
          <Link to="/expenses/new">
            <Button variant="primary" icon={<FiPlus />}>
              Add Expense
            </Button>
          </Link>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '8px', color: 'white' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: 0, opacity: 0.9 }}>Expenses (this page)</p>
                <h3 style={{ margin: '0.5rem 0 0 0', fontSize: '2rem' }}>{formatCurrency(pageTotalAmount)}</h3>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', opacity: 0.8 }}>
                  {total} expense{total !== 1 ? 's' : ''} total
                </p>
              </div>
            </div>
          </div>

          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Paid To</th>
                  <th>Amount</th>
                  <th>Payment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense) => (
                  <tr key={expense._id}>
                    <td>{format(new Date(expense.date), 'MMM dd, yyyy')}</td>
                    <td><strong>{expense.description}</strong></td>
                    <td>
                      <span className={getCategoryBadge(expense.category)}>
                        {expense.category?.charAt(0).toUpperCase() + expense.category?.slice(1)}
                      </span>
                    </td>
                    <td>{expense.paidTo ?? '—'}</td>
                    <td style={{ fontWeight: 700, color: 'var(--error-500)' }}>{formatCurrency(expense.amount)}</td>
                    <td>
                      {expense.paymentMethod && (
                        <span className={getPaymentMethodBadge(expense.paymentMethod)}>
                          {expense.paymentMethod.replace('_', ' ')}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="cell-actions">
                        <Link to={`/expenses/${expense._id}/edit`} className="action-btn action-btn-edit">
                          <FiEdit /> Edit
                        </Link>
                        <button type="button" onClick={() => handleDelete(expense._id)} className="action-btn action-btn-delete">
                          <FiTrash2 /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(pages > 1 || total > expenses.length || total > 0) && (
            <div className="pagination-bar">
              <span className="pagination-info">
                Showing {(currentPage - 1) * effectiveLimit + 1}–{Math.min(currentPage * effectiveLimit, total)} of {total}
              </span>
              <div className="pagination-limit">
                <label className="pagination-limit-label">Per page</label>
                <select value={limitOption} onChange={(e) => setLimitOption(e.target.value)} className="pagination-limit-select">
                  {LIMIT_OPTIONS.map((n) => (<option key={n} value={String(n)}>{n}</option>))}
                  <option value="custom">Custom</option>
                </select>
                {limitOption === 'custom' && (
                  <input type="number" min={1} max={100} value={customLimit} onChange={(e) => setCustomLimit(parseInt(e.target.value, 10) || 10)} className="pagination-limit-input" />
                )}
              </div>
              <div className="pagination-controls">
                <button type="button" className="pagination-btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
                <span className="pagination-info" style={{ padding: '0 0.75rem' }}>Page {currentPage} of {pages}</span>
                <button type="button" className="pagination-btn" onClick={() => setCurrentPage((p) => Math.min(pages, p + 1))} disabled={currentPage === pages}>Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Expenses;
